import { Buffer } from 'buffer';
import { generatePath } from 'react-router-dom';

import { EmptyTextData } from 'library/types';
import {
	UnitOVerview_Services_ServiceContracts,
	UnitOverviewPageUrl,
	UnitOverview_ServiceArea,
	connectServiceContractPath,
	connectServiceContractSearchParam,
	generateHealthCheckUrl,
	generateServiceProtocolUrl,
	serviceContractArea,
	serviceProtocolView,
	unitPublicIdSearchParam,
} from 'main/routes/urls/RouteUrls';

const CommissioningMarineDeliveryInspectionDocument = import.meta.env
	.VITE_COMISSIONING_DOCUMENT_MARINE;
const CommissioningIndustrialDeliveryInspectionDocument = import.meta.env
	.VITE_COMISSIONING_DOCUMENT_INDUSTRIAL;

const volvoShopPartsUrl = import.meta.env.VITE_VOLVO_PENTA_SHOP_URL;
export const volvoWOPUrl = import.meta.env.VITE_WOP_URL;

export const getCorrectPcUrl = (url: EmptyTextData) => {
	if (url == null) {
		return url;
	}

	const newUrl = new URL(url);
	newUrl.hostname = window.location.hostname;

	return newUrl.toString();
};

const getPcReturnUrlQuery = (returnUrl = window.location.href) =>
	`returnUrl=${Buffer.from(returnUrl).toString('base64')}`;

const pcHostUrl = getCorrectPcUrl(import.meta.env.VITE_PC_HOST);

export const publicationsUrl =
	getCorrectPcUrl(import.meta.env.VITE_URL_PUBLICATIONS) ?? '';
export const plannerUrl =
	getCorrectPcUrl(import.meta.env.VITE_URL_MAINTENANCE_PLANNER) ?? '';
export const calculatorUrl =
	getCorrectPcUrl(import.meta.env.VITE_URL_SERVICE_CALCULATOR) ?? '';
export const calculationsUrl =
	getCorrectPcUrl(import.meta.env.VITE_URL_MY_CALCULATIONS) ?? '';
export const swkUrl = getCorrectPcUrl(import.meta.env.VITE_URL_SWK) ?? '';
export const repairUrl = getCorrectPcUrl(import.meta.env.VITE_URL_REPAIR) ?? '';

export const publicationCommissioningMarineCheckListUrl = `${publicationsUrl}?st=publicationNumber&sp=${CommissioningMarineDeliveryInspectionDocument}`;
export const publicationCommissioningIndustrialCheckListUrl = `${publicationsUrl}?st=publicationNumber&sp=${CommissioningIndustrialDeliveryInspectionDocument}`;

export const getPublicationProductIndividualUrl = (serialNumber: string) =>
	`${publicationsUrl}?st=serialnumber&sp=${serialNumber}`;

export const getPublicationOfflineServiceProtocolUrl = (
	serialNumber: string,
	language: string | null
) =>
	`${publicationsUrl}?st=serialnumber&sp=${serialNumber}&r[0].f=PublicationType&r[0].v=Service%20Protocol&r[1].f=Language&r[1].v=${language ?? 'en-US'}`;

export const getPublicationOnlineServiceManualUrl = (
	serialNumber: string,
	language: string | null
) =>
	`${publicationsUrl}?st=serialnumber&sp=${serialNumber}&r[0].f=PublicationType&r[0].v=Service%20and%20Maintenance%20Manual&r[1].f=Language&r[1].v=${language ?? 'en-US'}`;

export const getVolvoShopPartsBySNUrl = (serialNumber: string) =>
	`${volvoShopPartsUrl}/search?serialnumber=${serialNumber}`;

export const getUnitOverviewLink = (unitId: number) =>
	`${pcHostUrl}/UnitOverview/${unitId}`;

export const getUnitActionsLink = (
	mvcAction: string,
	id: number,
	additionalParams?: Record<string, string>,
	returnUrl?: string
) => {
	const params = new URLSearchParams(getPcReturnUrlQuery(returnUrl));

	if (additionalParams) {
		Object.entries(additionalParams).forEach(([key, value]) => {
			params.append(key, value);
		});
	}

	const url = new URL(`${pcHostUrl}/${mvcAction}/${id}`);
	url.search = params.toString();

	return url.toString();
};

export const getUnitPrintPreviewLink = (id: string | number) =>
	`${pcHostUrl}/UnitOverview/PrintPdfWithCoverages/${encodeURIComponent(id)}`;

export const getLinkOpenServiceProtocolView = (
	id: number,
	openInNewWindow: boolean = false
) =>
	generatePath(
		`${openInNewWindow ? import.meta.env.BASE_URL : ''}/${serviceProtocolView}`,
		{
			serviceProtocolId: id.toString(),
		}
	);

export const getLinkOpenInitiatedServiceProtocol = (
	serviceProtocolId: number,
	drivelineId: number,
	openInNewWindow: boolean = false
) =>
	generatePath(
		`${openInNewWindow ? import.meta.env.BASE_URL : ''}/${generateServiceProtocolUrl}?drivelineId=${drivelineId}`,
		{
			serviceProtocolId: serviceProtocolId.toString(),
		}
	);

export const getLinkCreateNewServiceProtocol = (
	drivelineId: number,
	serviceScheduledDate?: Date | null,
	openInNewWindow: boolean = false
) => {
	const andServiceScheduledDateParam = serviceScheduledDate
		? `&serviceDate=${serviceScheduledDate.toISOString()}`
		: '';
	const baseUrl = openInNewWindow ? import.meta.env.BASE_URL : '';

	return generatePath(
		`${baseUrl}/${generateServiceProtocolUrl}?drivelineId=${drivelineId}${andServiceScheduledDateParam}`
	);
};

export const getChangeLanguageLink = () =>
	`${pcHostUrl}/Utility/RefreshSessionAndCultureCookie`;

const getRepairActionLink = (action: string) => `${repairUrl}/${action}`;

const getRepairLink = (action: string, repairId: number) =>
	`${getRepairActionLink(action)}/${repairId}?${getPcReturnUrlQuery()}`;

export const getRepairViewLink = (repairId: number) =>
	getRepairLink('View', repairId);

export const getRepairEditLink = (repairId: number) =>
	getRepairLink('Edit', repairId);

export const getRepairAddLink = (serialNumber: string) =>
	`${getRepairActionLink('Add')}?serialNumber=${serialNumber}&${getPcReturnUrlQuery()}`;

export const getRepairPrintLink = (repairId: number, serialNumber: string) =>
	`${pcHostUrl}/ProductIndividual/PrintRepair?repairId=${repairId}&serialNumber=${serialNumber}`;

export const pcPublicUrl = import.meta.env.VITE_URL_PC_PUBLIC ?? '';

export const ManualUrl = `${pcPublicUrl}/ownerinformation/ManualAndSpecification?displayLang=en-US&searchLang=en-US`;

export const RecallUrl = `${pcPublicUrl}/ownerinformation/RecallInformation?displayLang=en-US&searchLang=en-US`;

export const getLinkGenerateHealthCheck = (
	unitPublicId: string, // have to use public id in the frontend, because internal id could be translated to hiloid
	healthCheckId?: number | null,
	openInNewWindow = false
) => {
	const baseUrl = openInNewWindow ? import.meta.env.BASE_URL : '';

	return generatePath(`${baseUrl}${generateHealthCheckUrl(unitPublicId)}`, {
		healthCheckId: healthCheckId?.toString() ?? '',
	});
};

export const getLinkServiceContracts = (unitPublicId: string) =>
	generatePath(
		`${UnitOverviewPageUrl}/${UnitOverview_ServiceArea}/${UnitOVerview_Services_ServiceContracts}`,
		{ unitId: unitPublicId }
	);

export const getLinkConnectServiceContract = (
	unitPublicId: string, // have to use public id in the frontend, because internal id could be translated to hiloid
	serviceContractId: string
) => {
	const searchParams = new URLSearchParams({
		[unitPublicIdSearchParam]: unitPublicId,
		[connectServiceContractSearchParam]: serviceContractId,
	});

	return `/${serviceContractArea}/${connectServiceContractPath}?${searchParams.toString()}`;
};
